/**
 * Anchor links that point at a contact form.
 *
 * On mobile they land on the section heading, which can push the form itself
 * out of view when the heading and intro copy are long, so aim at the form.
 * On every device the jump is held in place until the page stops resizing —
 * sections below (product recommendations especially) load on first view and
 * would otherwise leave the scroll short.
 */
(() => {
  if (window.formAnchorScroll) return;

  const MOBILE_QUERY = '(max-width: 768px)';
  const FORM_SELECTOR = '.contact-form__form';
  const SETTLE_INTERVAL = 100;
  const SETTLE_TIMEOUT = 3000;

  const isMobile = () => window.matchMedia(MOBILE_QUERY).matches;
  const reduceMotion = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  function scrollOffset() {
    const styles = getComputedStyle(document.documentElement);
    const offset = parseFloat(styles.getPropertyValue('--anchor-scroll-offset'));
    if (!Number.isNaN(offset)) return offset;
    return (parseFloat(styles.getPropertyValue('--header-height')) || 0) + 16;
  }

  function formIn(element) {
    if (!element || !element.querySelector) return null;
    if (element.matches?.(FORM_SELECTOR)) return element;
    return element.querySelector(FORM_SELECTOR);
  }

  /**
   * The anchor id can sit on the form section, on an empty anchor span placed
   * just before it, or on a wrapper further up the tree.
   */
  function formFor(target) {
    if (!target) return null;

    let form = formIn(target);
    if (form) return form;

    let sibling = target.nextElementSibling;
    for (let i = 0; i < 3 && sibling; i += 1) {
      form = formIn(sibling);
      if (form) return form;
      sibling = sibling.nextElementSibling;
    }

    return formIn(target.closest?.('.contact-form-section, .shopify-section'));
  }

  /** The element an anchor should scroll to: the form itself on mobile. */
  function resolveTarget(target) {
    if (!target || !isMobile()) return target;
    return formFor(target) || target;
  }

  /**
   * Keep correcting the scroll position until the target holds still.
   *
   * Content below the form — product recommendations that fetch on first view,
   * lazily loaded media, reveal animations — can resize the page while the
   * smooth scroll is running, or the page can simply be too short to reach the
   * target until that content arrives. Either way the first jump stops short,
   * so re-aim until the target settles at the intended offset.
   */
  function settleOn(target) {
    const cancelEvents = ['wheel', 'touchstart', 'keydown', 'mousedown', 'focusin'];
    const deadline = Date.now() + SETTLE_TIMEOUT;
    let cancelled = false;
    let lastY = window.scrollY;

    const cancel = () => { cancelled = true; };
    const stop = () => cancelEvents.forEach((type) => window.removeEventListener(type, cancel));
    cancelEvents.forEach((type) => window.addEventListener(type, cancel, { passive: true }));

    const check = () => {
      if (cancelled || !target.isConnected) {
        stop();
        return;
      }

      const y = window.scrollY;
      const moving = Math.abs(y - lastY) > 1;
      const drift = target.getBoundingClientRect().top - scrollOffset();
      lastY = y;

      // Only correct once the in-flight scroll has stopped, otherwise the
      // correction fights the animation.
      if (!moving && Math.abs(drift) > 2) {
        const behavior = Math.abs(drift) > 60 && !reduceMotion() ? 'smooth' : 'auto';
        window.scrollTo({ top: Math.max(0, y + drift), behavior });
      }

      // Keep watching for the whole window: sections below can finish loading
      // well after the scroll itself has stopped.
      if (Date.now() > deadline) {
        stop();
        return;
      }

      window.setTimeout(check, SETTLE_INTERVAL);
    };

    window.setTimeout(check, SETTLE_INTERVAL);
  }

  function scrollToTarget(target, options = {}) {
    if (!target) return;
    const behavior = reduceMotion() ? 'auto' : 'smooth';
    const top = target.getBoundingClientRect().top + window.scrollY - scrollOffset();
    window.scrollTo({ top: Math.max(0, top), behavior });

    if (options.settle === false) return;
    settleOn(target);
  }

  function elementForHash(hash) {
    if (!hash || hash.length < 2) return null;
    try {
      return document.getElementById(decodeURIComponent(hash.slice(1)));
    } catch (error) {
      return null;
    }
  }

  function onClick(event) {
    if (event.defaultPrevented || event.button !== 0 || event.metaKey || event.ctrlKey
      || event.shiftKey || event.altKey) return;

    const link = event.target.closest('a[href]');
    if (!link || link.target === '_blank') return;
    // Handled by delivery-info-links.js, which also fills in the product SKU.
    if (link.matches('[data-backorder-contact], [data-delivery-contact]')) return;

    const href = link.getAttribute('href');
    if (!href || !href.includes('#')) return;

    let url;
    try {
      url = new URL(href, window.location.href);
    } catch (error) {
      return;
    }
    if (url.origin !== window.location.origin || url.pathname !== window.location.pathname) return;

    const target = elementForHash(url.hash);
    // Only take over links that point at a contact form; everything else keeps
    // the browser's native anchor behaviour.
    if (!target || !formFor(target)) return;

    event.preventDefault();
    scrollToTarget(resolveTarget(target));
    window.history.replaceState(null, '', url.hash);
  }

  function onLoadHash() {
    const target = elementForHash(window.location.hash);
    if (!target || !formFor(target)) return;
    scrollToTarget(resolveTarget(target));
  }

  window.formAnchorScroll = { resolveTarget, scrollToTarget, scrollOffset, isMobile };

  document.addEventListener('click', onClick);
  window.addEventListener('hashchange', onLoadHash);

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', onLoadHash, { once: true });
  } else {
    onLoadHash();
  }
})();
